import { Button, EmptyState, Table, useDrawer } from '@iveco-ti/iveco-ds'
import { Plus } from 'lucide-react'
import { HeadProvider, Title } from 'react-head'
import { useTranslation } from 'react-i18next'

import { Can } from '@/common/components/Can'
import { ExportDropdown } from '@/common/components/ExportDropdown'
import { FilterButton } from '@/common/components/FilterButton'
import { OrderByDropdown } from '@/common/components/OrderByDropdown'
import { useDownloadExport } from '@/common/hooks/useDownloadExport'
import { AccountRowSkeleton } from './components/AccountRowSkeleton'
import { AccountTableRow } from './components/AccountTableRow'
import { DrawerEditAccount } from './components/DrawerEditAccount'
import { DrawerFilterAccounts } from './components/DrawerFilterAccounts'
import { DrawerManageApplications } from './components/DrawerManageApplications'
import { DrawerNewAccount } from './components/DrawerNewAccount'
import { DrawerViewAccount } from './components/DrawerViewAccount'
import { usePaginateAccounts } from './hooks/usePaginateAccounts'

export default function Accounts() {
  const { t } = useTranslation(['sso', 'common'])
  const {
    accounts,
    accountsOrderBy,
    isLoadingAccounts,
    accountsPaginated,
    accountsPerPage,
    accountsBadges,
    filtersQty,
    handleAccountsSearch,
    handleChangeOrder,
    handleChangePage,
  } = usePaginateAccounts()
  const { downloadExport, isDownloadingExport } = useDownloadExport({
    route: '/account/report',
  })
  const drawerFilterAccounts = useDrawer('drawer-filter-accounts')
  const drawerNewAccount = useDrawer('drawer-new-account')

  const ORDER_BY_OPTIONS = [
    {
      label: t('sso:accounts.table.order_dropdown.fields.title.label'),
      field: 'title',
    },
    {
      label: t('sso:accounts.table.order_dropdown.fields.key.label'),
      field: 'accountKey',
    },
    {
      label: t('sso:accounts.table.order_dropdown.fields.country.label'),
      field: 'country',
    },
  ]

  const TABLE_COLUMNS = [
    {
      label: t('sso:accounts.table.table_head.column_first.label'),
      order: 'title',
    },
    {
      label: t('sso:accounts.table.table_head.column_second.label'),
      order: 'country',
      badge: accountsBadges.country,
    },
    {
      label: t('sso:accounts.table.table_head.column_third.label'),
      order: 'locale',
      badge: accountsBadges.locale,
    },
    {
      label: t('sso:accounts.table.table_head.column_fourth.label'),
    },
    {
      label: t('sso:accounts.table.table_head.column_fifth.label'),
      end: true,
    },
  ]

  return (
    <>
      <div className="overflow-y-auto h-full shadow-sm rounded-xl bg-white">
        <HeadProvider>
          <Title>{t('sso:accounts.browser.title')}</Title>
        </HeadProvider>

        <Table.Root>
          <Table.Search
            searchName="search"
            searchPlaceholder={t(
              'common:authenticated_layout.search_input.placeholder',
            )}
            onSearch={handleAccountsSearch}
          >
            <OrderByDropdown
              selected={accountsOrderBy}
              onChangeOrder={handleChangeOrder}
              options={ORDER_BY_OPTIONS}
            />

            <ExportDropdown
              onExportCSV={() => downloadExport({}, 'csv')}
              onExportXLSX={() => downloadExport({}, 'xlsx')}
              isExporting={isDownloadingExport}
            />

            <FilterButton
              open={drawerFilterAccounts.open}
              filters={filtersQty}
            />

            <Can I="create" a="Account">
              <Button.Root onClick={drawerNewAccount.open}>
                <Button.Icon icon={Plus} />
              </Button.Root>
            </Can>
          </Table.Search>

          {isLoadingAccounts && <AccountRowSkeleton />}

          {!accounts.length && !isLoadingAccounts && (
            <EmptyState
              title={t('sso:accounts.empty_state.title')}
              description={t('sso:accounts.empty_state.description')}
            />
          )}

          <Table.Content>
            {!isLoadingAccounts && !!accounts.length && (
              <>
                <Table.Head
                  hideCheckbox
                  cols={TABLE_COLUMNS}
                  onOrderBy={(order) => handleChangeOrder(order, 'asc')}
                />

                <Table.Body>
                  {accounts.map((account) => (
                    <AccountTableRow key={account.id} account={account} />
                  ))}
                </Table.Body>
              </>
            )}
          </Table.Content>

          <Table.Footer
            page={accountsPaginated?.currentPage || 1}
            totalItems={accountsPaginated?.totalItems || 0}
            totalItemsLabel={t(
              'sso:accounts.table.table_footer.total_registers.label',
            )}
            totalPages={accountsPaginated?.totalPages || 1}
            ofText={t('common:components.table_footer.pagination.label')}
            onChangePage={handleChangePage}
            itemsPerPage={accountsPerPage}
          />
        </Table.Root>
      </div>

      <DrawerFilterAccounts />
      <DrawerNewAccount />
      <DrawerEditAccount />
      <DrawerViewAccount />
      <DrawerManageApplications />
    </>
  )
}
